import React, { useMemo, useRef, useState } from 'react';
import { FiMenu } from 'react-icons/fi';

const DEFAULT_PROP = { name: '', type: 'text', required: false, show_name: true, as_title: false, as_subtitle: false, meta: null };

function normalizeMobileProps(rawProps) {
  if (!Array.isArray(rawProps)) return [];
  return rawProps.map((mp) => ({
    column_idx: typeof mp?.column_idx === 'number' ? mp.column_idx : 0,
    prop_idx:   typeof mp?.prop_idx   === 'number' ? mp.prop_idx   : 0,
    visible:    mp?.visible == null ? true : !!mp.visible,
  }));
}

// Keeps mobile.properties in sync with current widescreen columns.
// Removes stale refs, appends newly added props (visible=true).
export function syncMobileProps(columns, currentMobileProps) {
  const validKeys = new Set();
  for (let cIdx = 0; cIdx < columns.length; cIdx++) {
    for (let pIdx = 0; pIdx < (columns[cIdx].properties || []).length; pIdx++) {
      validKeys.add(`${cIdx}:${pIdx}`);
    }
  }
  const kept = currentMobileProps.filter((mp) => validKeys.has(`${mp.column_idx}:${mp.prop_idx}`));
  const keptKeys = new Set(kept.map((mp) => `${mp.column_idx}:${mp.prop_idx}`));
  const added = [];
  for (let cIdx = 0; cIdx < columns.length; cIdx++) {
    for (let pIdx = 0; pIdx < (columns[cIdx].properties || []).length; pIdx++) {
      if (!keptKeys.has(`${cIdx}:${pIdx}`)) {
        added.push({ column_idx: cIdx, prop_idx: pIdx, visible: true });
      }
    }
  }
  return [...kept, ...added];
}

export function normalize(struct) {
  const base = struct && typeof struct === 'object' ? struct : {};
  const cols = Array.isArray(base.columns) ? base.columns : [];
  const normalizedCols = cols.map((col) => {
    const properties = Array.isArray(col?.properties) ? col.properties : [];
    return {
      properties: properties.map((p) => {
        const type = ['image', 'text', 'line'].includes((p?.type || '').toLowerCase()) ? (p.type || '').toLowerCase() : DEFAULT_PROP.type;

        // meta is only supported for type 'line'. For other types meta MUST be null.
        let meta = null;
        if (type === 'line') {
          const m = (p && p.meta && typeof p.meta === 'object') ? p.meta : {};
          const allowedFontSizes = ['heading', 'sub-heading', 'text'];
          const fs = String(m.fontSize ?? '').toLowerCase();
          meta = {
            prefix: String(m.prefix ?? ''),
            suffix: String(m.suffix ?? ''),
            fontSize: allowedFontSizes.includes(fs) ? fs : 'text',
          };
        }

        return {
          name: String(p?.name ?? DEFAULT_PROP.name),
          type,
          required: p?.required !== undefined ? !!p.required : DEFAULT_PROP.required,
          show_name: p?.show_name !== undefined ? !!p.show_name : DEFAULT_PROP.show_name,
          as_title: p?.as_title !== undefined ? !!p.as_title : DEFAULT_PROP.as_title,
          as_subtitle: p?.as_subtitle !== undefined ? !!p.as_subtitle : DEFAULT_PROP.as_subtitle,
          meta,
        };
      }),
    };
  });

  const mobileBase = base.mobile && typeof base.mobile === 'object' ? base.mobile : {};
  const mobile = { properties: normalizeMobileProps(mobileBase.properties) };

  return { columns: normalizedCols, mobile };
}

const GIS_STYLES = `
  /* Container */
  .gis-container {
    border-radius: 8px;
    overflow: hidden;
    margin: 12px 0;
    background: var(--card-bg, #fff);
  }

  /* Toolbar */
  .gis-toolbar {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 12px;
    background: rgba(49,59,114,0.05);
    border-bottom: 1px solid rgba(49,59,114,0.1);
  }
  .gis-tabs { display: flex; gap: 3px; }
  .gis-tab-btn {
    background: rgba(49,59,114,0.07);
    border: none;
    border-radius: 5px;
    padding: 3px 11px;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.03em;
    color: var(--navy);
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.12s, background 0.12s;
    font-family: inherit;
  }
  .gis-tab-btn:hover:not(.gis-tab-btn--active) { opacity: 0.9; }
  .gis-tab-btn--active {
    background: linear-gradient(135deg, var(--purple) 0%, var(--navy) 100%);
    color: #fff;
    opacity: 1;
  }
  /* "+ column" — matches structure-add-child-btn */
  .gis-add-col-btn {
    display: inline-flex;
    align-items: center;
    background: transparent;
    border: none;
    color: var(--navy);
    padding: 3px 6px;
    font-size: 0.8em;
    font-family: inherit;
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.15s;
    margin-left: auto;
  }
  .gis-add-col-btn:hover { opacity: 1; }

  .gis-content { padding: 10px; }
  .gis-empty-hint {
    color: var(--green);
    font-size: 0.82rem;
    opacity: 0.55;
    text-align: center;
    padding: 16px 0;
  }

  /* Column — left-accent trough to group properties */
  .gis-column {
    border-left: 3px solid rgba(49,59,114,0.2);
    border-radius: 0 6px 6px 0;
    margin-bottom: 8px;
    background: rgba(49,59,114,0.04);
  }
  .gis-column-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 8px 4px 10px;
    background: rgba(49,59,114,0.07);
    border-radius: 0 6px 0 0;
  }
  .gis-column-label {
    font-size: 0.67rem;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--navy);
    opacity: 0.4;
  }
  .gis-column-body { padding: 0 0 6px; }

  /* Property items — flat list with hairline separators (mirrors structure-node) */
  .gis-prop-card {
    position: relative;
    cursor: default;
    transition: background 0.12s;
  }
  .gis-prop-card + .gis-prop-card {
    border-top: 1px solid rgba(98,168,124,0.45);
  }
  .gis-prop-card:hover { background: rgba(49,59,114,0.04); }
  .gis-prop-card--dragging { opacity: 0.3; }

  /* Drop indicator — matches structure-node--drag-before */
  .gis-drop-line {
    height: 2px;
    background: var(--navy);
    opacity: 0.5;
    margin: 0;
  }

  /* Drag handle — same pattern as .structure-drag-handle */
  .gis-drag-handle {
    display: inline-flex;
    align-items: center;
    color: var(--green);
    opacity: 0;
    cursor: grab;
    flex-shrink: 0;
    padding: 0 2px;
    transition: opacity 0.12s;
  }
  .gis-prop-card:hover .gis-drag-handle { opacity: 0.45; }
  .gis-drag-handle:active { cursor: grabbing; }

  /* Mobile drag handle */
  .gis-mobile-drag-handle {
    display: inline-flex;
    align-items: center;
    color: var(--green);
    opacity: 0;
    cursor: grab;
    flex-shrink: 0;
    padding: 0 2px;
    transition: opacity 0.12s;
  }
  .gis-mobile-prop:hover .gis-mobile-drag-handle { opacity: 0.45; }
  .gis-mobile-drag-handle:active { cursor: grabbing; }

  .gis-prop-main {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.45rem 0.55rem;
  }
  .gis-prop-name { flex: 1 1 120px; min-width: 0; }
  .gis-prop-type { flex: 0 0 72px; }

  /* Inline inputs — strip Bootstrap form-control styles */
  .gis-container .form-control,
  .gis-container .form-control:focus,
  .gis-container .form-control-sm,
  .gis-container .form-control-sm:focus {
    background: transparent !important;
    border: none !important;
    border-bottom: 1px solid rgba(49,59,114,0.15) !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    padding: 1px 2px !important;
    height: auto !important;
    font-size: 0.84rem;
    color: var(--navy);
    transition: border-color 0.12s;
  }
  .gis-container .form-control:focus,
  .gis-container .form-control-sm:focus {
    border-bottom-color: var(--navy) !important;
    outline: none;
  }
  .gis-container .form-control::placeholder { color: var(--green); opacity: 0.35; }
  .gis-container select.form-control,
  .gis-container select.form-control-sm {
    padding-right: 14px !important;
    -webkit-appearance: none;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5' viewBox='0 0 8 5'%3E%3Cpath d='M0 0l4 5 4-5z' fill='%23313b72' opacity='.35'/%3E%3C/svg%3E") !important;
    background-repeat: no-repeat !important;
    background-position: right 2px center !important;
    cursor: pointer;
  }
  @media (prefers-color-scheme: dark) {
    .gis-container .form-control,
    .gis-container .form-control-sm {
      color: rgba(255,255,255,0.75) !important;
      border-bottom-color: rgba(255,255,255,0.15) !important;
    }
    .gis-container .form-control:focus,
    .gis-container .form-control-sm:focus {
      border-bottom-color: rgba(255,255,255,0.5) !important;
    }
    .gis-container .form-control::placeholder { color: rgba(255,255,255,0.22); }
    .gis-container select.form-control,
    .gis-container select.form-control-sm {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5' viewBox='0 0 8 5'%3E%3Cpath d='M0 0l4 5 4-5z' fill='%23ffffff' opacity='.35'/%3E%3C/svg%3E") !important;
    }
  }

  /* Remove button — matches .structure-delete-btn */
  .gis-x-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    background: transparent;
    border: 1px solid rgba(160,18,51,0.3);
    border-radius: 5px;
    color: #a01233;
    font-size: 0.95rem;
    line-height: 1;
    cursor: pointer;
    transition: background 0.12s, border-color 0.12s, color 0.12s;
    font-family: inherit;
    padding: 0;
  }
  .gis-x-btn:hover { background: #a01233; border-color: #a01233; color: #fff; }

  /* Flags row */
  .gis-prop-flags {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0 8px;
    padding: 0 0.55rem 0.4rem 1.6rem;
  }
  .gis-flag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--navy);
    cursor: pointer;
    user-select: none;
    opacity: 0.45;
    transition: opacity 0.12s;
    padding: 2px 0;
  }
  .gis-flag:hover { opacity: 0.85; }
  .gis-flag input[type="checkbox"] { cursor: pointer; margin: 0; }

  /* Inline meta inputs (prefix / suffix / font-size) — sits in the flags row */
  .gis-meta-inline {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-left: auto;
  }

  /* Add property — matches .structure-add-child-btn */
  .gis-add-prop-btn {
    display: inline-flex;
    align-items: center;
    width: 100%;
    padding: 5px 10px;
    background: transparent;
    border: none;
    color: var(--navy);
    opacity: 0.55;
    font-size: 0.8em;
    font-family: inherit;
    cursor: pointer;
    transition: opacity 0.15s;
    border-top: 1px solid rgba(56,53,43,0.08);
    margin-top: 2px;
  }
  .gis-add-prop-btn:hover { opacity: 1; }

  /* Mobile props */
  .gis-mobile-prop {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.45rem 0.55rem;
    transition: background 0.12s, opacity 0.12s;
    cursor: default;
  }
  .gis-mobile-prop + .gis-mobile-prop { border-top: 1px solid rgba(56,53,43,0.1); }
  .gis-mobile-prop:hover { background: rgba(49,59,114,0.04); }
  .gis-mobile-prop--hidden { opacity: 0.35; }
  .gis-mobile-prop--dragging { opacity: 0.3; }
  .gis-mobile-prop--over { box-shadow: 0 -2px 0 var(--navy); }
  .gis-mobile-prop-name {
    flex: 1;
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--navy);
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Dark mode */
  @media (prefers-color-scheme: dark) {
    .gis-container { background: var(--dark-surface); }
    .gis-toolbar { background: rgba(255,255,255,0.04); border-bottom-color: rgba(255,255,255,0.07); }
    .gis-tab-btn { background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.6); }
    .gis-tab-btn:hover:not(.gis-tab-btn--active) { background: rgba(255,255,255,0.1); opacity: 1; }
    .gis-add-col-btn { color: rgba(255,255,255,0.6); }
    .gis-column { border-left-color: rgba(255,255,255,0.12); background: rgba(255,255,255,0.03); }
    .gis-column-head { background: rgba(255,255,255,0.05); }
    .gis-column-label { color: rgba(255,255,255,0.45); }
    .gis-prop-card + .gis-prop-card { border-top-color: rgba(98,168,124,0.3); }
    .gis-prop-card:hover { background: rgba(255,255,255,0.04); }
    .gis-drop-line { background: rgba(255,255,255,0.5); }
    .gis-drag-handle { color: rgba(255,255,255,0.5); }
    .gis-mobile-drag-handle { color: rgba(255,255,255,0.5); }
    .gis-flag { color: rgba(255,255,255,0.5); opacity: 1; }
    .gis-x-btn { border-color: rgba(200,80,80,0.3); color: rgba(200,80,80,0.7); }
    .gis-x-btn:hover { background: #b84040; border-color: #b84040; color: #fff; }
    .gis-add-prop-btn { color: rgba(255,255,255,0.5); border-top-color: rgba(255,255,255,0.07); }
    .gis-mobile-prop + .gis-mobile-prop { border-top-color: rgba(255,255,255,0.07); }
    .gis-mobile-prop:hover { background: rgba(255,255,255,0.04); }
    .gis-mobile-prop--over { box-shadow: 0 -2px 0 rgba(255,255,255,0.4); }
    .gis-mobile-prop-name { color: rgba(255,255,255,0.75); }
  }
`;

const GalleryItemStructureEditor = ({ value, onChange, className = '' }) => {
  const struct = useMemo(() => normalize(value), [value]);
  const [activeTab, setActiveTab] = useState('widescreen');

  // Widescreen drag state
  const [propDrag, setPropDrag] = useState(null); // { cIdx, pIdx }
  const [propOver, setPropOver] = useState(null); // { cIdx, pIdx }
  const propCardRefs = useRef({});

  // Mobile drag state
  const [mobileDrag, setMobileDrag] = useState(-1);
  const [mobileOver, setMobileOver] = useState(-1);
  const mobileRefs = useRef([]);

  const emit = (nextColumns) => {
    const normalized = normalize({ ...struct, columns: nextColumns });
    const syncedMobileProps = syncMobileProps(normalized.columns, normalized.mobile.properties);
    onChange?.({ ...normalized, mobile: { properties: syncedMobileProps } });
  };

  const emitMobile = (nextMobileProps) => {
    onChange?.({ ...struct, mobile: { properties: nextMobileProps } });
  };

  const addColumn = () => emit([...struct.columns, { properties: [] }]);
  const removeColumn = (idx) => emit(struct.columns.filter((_, i) => i !== idx));
  const addProperty = (cIdx) =>
    emit(struct.columns.map((c, i) => (i === cIdx ? { properties: [...c.properties, { ...DEFAULT_PROP }] } : c)));
  const removeProperty = (cIdx, pIdx) =>
    emit(struct.columns.map((c, i) =>
      i === cIdx ? { properties: c.properties.filter((_, j) => j !== pIdx) } : c
    ));

  const updateProperty = (cIdx, pIdx, field, val) =>
    emit(struct.columns.map((c, i) => {
      const props = c.properties.map((p, j) => {
        const isTarget = i === cIdx && j === pIdx;
        if (!isTarget) {
          if (field === 'as_title' && val === true) return { ...p, as_title: false };
          if (field === 'as_subtitle' && val === true) return { ...p, as_subtitle: false };
          return p;
        }
        if (field === 'type') {
          const nextType = String(val || '').toLowerCase();
          if (nextType === 'line') {
            return { ...p, type: 'line', meta: p.meta && typeof p.meta === 'object'
                    ? { prefix: p.meta.prefix || '', suffix: p.meta.suffix || '', fontSize: p.meta.fontSize || 'text' }
                    : { prefix: '', suffix: '', fontSize: 'text' } };
          }
          return { ...p, type: nextType || 'text', meta: null, as_title: false, as_subtitle: false };
        }
        return { ...p, [field]: val };
      });
      return { properties: props };
    }));

  const reorderProperty = (cIdx, fromIdx, toIdx) => {
    const props = [...(struct.columns[cIdx]?.properties || [])];
    const [moved] = props.splice(fromIdx, 1);
    props.splice(toIdx, 0, moved);
    emit(struct.columns.map((c, i) => i === cIdx ? { properties: props } : c));
  };

  // Widescreen drag handlers
  const onPropDragStart = (cIdx, pIdx, e) => {
    setPropDrag({ cIdx, pIdx });
    setPropOver({ cIdx, pIdx });
    try { e.dataTransfer.setData('text/plain', `${cIdx}:${pIdx}`); } catch (_) {}
    e.dataTransfer.effectAllowed = 'move';
    const el = propCardRefs.current[`${cIdx}:${pIdx}`];
    if (el && e.dataTransfer.setDragImage) e.dataTransfer.setDragImage(el, 20, 12);
  };
  const onPropDragEnter = (cIdx, pIdx) => {
    if (!propOver || propOver.cIdx !== cIdx || propOver.pIdx !== pIdx) setPropOver({ cIdx, pIdx });
  };
  const onPropDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onPropDrop = (cIdx, pIdx, e) => {
    e.preventDefault();
    if (propDrag && propDrag.cIdx === cIdx && propDrag.pIdx !== pIdx) {
      reorderProperty(cIdx, propDrag.pIdx, pIdx);
    }
    setPropDrag(null);
    setPropOver(null);
  };
  const onPropDragEnd = () => { setPropDrag(null); setPropOver(null); };

  const displayMobileProps = useMemo(
    () => syncMobileProps(struct.columns, struct.mobile.properties),
    [struct.columns, struct.mobile.properties]
  );

  const toggleMobileProp = (idx, visible) => {
    emitMobile(displayMobileProps.map((mp, i) => i === idx ? { ...mp, visible } : mp));
  };

  const reorderMobileProp = (fromIdx, toIdx) => {
    const props = [...displayMobileProps];
    const [moved] = props.splice(fromIdx, 1);
    props.splice(toIdx, 0, moved);
    emitMobile(props);
  };

  // Mobile drag handlers
  const onMobileDragStart = (idx, e) => {
    setMobileDrag(idx);
    setMobileOver(idx);
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch (_) {}
    e.dataTransfer.effectAllowed = 'move';
    const el = mobileRefs.current[idx];
    if (el && e.dataTransfer.setDragImage) e.dataTransfer.setDragImage(el, 20, 12);
  };
  const onMobileDragEnter = (idx) => { if (idx !== mobileOver) setMobileOver(idx); };
  const onMobileDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onMobileDrop = (idx, e) => {
    e.preventDefault();
    if (mobileDrag !== -1 && mobileDrag !== idx) reorderMobileProp(mobileDrag, idx);
    setMobileDrag(-1);
    setMobileOver(-1);
  };
  const onMobileDragEnd = () => { setMobileDrag(-1); setMobileOver(-1); };

  const getMobilePropLabel = (mp) => {
    const prop = struct.columns[mp.column_idx]?.properties[mp.prop_idx];
    if (!prop) return '(removed)';
    return prop.name || `Col ${mp.column_idx + 1} / Prop ${mp.prop_idx + 1}`;
  };

  const getMobilePropType = (mp) =>
    struct.columns[mp.column_idx]?.properties[mp.prop_idx]?.type || '';

  return (
    <div className={`gis-container${className ? ' ' + className : ''}`}>
      <style>{GIS_STYLES}</style>

      <div className="gis-toolbar">
        <div className="gis-tabs">
          <button type="button" className={`gis-tab-btn${activeTab === 'widescreen' ? ' gis-tab-btn--active' : ''}`} onClick={() => setActiveTab('widescreen')}>Widescreen</button>
          <button type="button" className={`gis-tab-btn${activeTab === 'mobile' ? ' gis-tab-btn--active' : ''}`} onClick={() => setActiveTab('mobile')}>Mobile</button>
        </div>
        {activeTab === 'widescreen' && (
          <button type="button" className="gis-add-col-btn" onClick={addColumn}>+ column</button>
        )}
      </div>

      <div className="gis-content">
        {activeTab === 'widescreen' && (
          <>
            {struct.columns.length === 0 && (
              <div className="gis-empty-hint">No columns yet. Add the first column.</div>
            )}

            {struct.columns.map((col, cIdx) => (
              <div key={cIdx} className="gis-column">
                <div className="gis-column-head">
                  <span className="gis-column-label">Column {cIdx + 1}</span>
                  <button type="button" className="gis-x-btn" onClick={() => removeColumn(cIdx)} title="Remove column">×</button>
                </div>
                <div className="gis-column-body">
                  {(col.properties || []).map((p, pIdx) => {
                    const isDragging = propDrag?.cIdx === cIdx && propDrag?.pIdx === pIdx;
                    const isOver = propOver?.cIdx === cIdx && propOver?.pIdx === pIdx && propDrag && !isDragging;
                    const metaKey = `${cIdx}:${pIdx}`;
                    return (
                      <div
                        key={pIdx}
                        ref={(el) => { propCardRefs.current[metaKey] = el; }}
                        className={`gis-prop-card${isDragging ? ' gis-prop-card--dragging' : ''}`}
                        draggable
                        onDragStart={(e) => onPropDragStart(cIdx, pIdx, e)}
                        onDragEnter={() => onPropDragEnter(cIdx, pIdx)}
                        onDragOver={onPropDragOver}
                        onDrop={(e) => onPropDrop(cIdx, pIdx, e)}
                        onDragEnd={onPropDragEnd}
                      >
                        {isOver && <div className="gis-drop-line" />}
                        <div className="gis-prop-main">
                          <span className="gis-drag-handle"><FiMenu size={11} /></span>
                          <input
                            type="text"
                            className="form-control form-control-sm gis-prop-name"
                            value={p.name}
                            onChange={(e) => updateProperty(cIdx, pIdx, 'name', e.target.value)}
                            placeholder="Property name"
                          />
                          <select
                            className="form-control form-control-sm gis-prop-type"
                            value={p.type}
                            onChange={(e) => updateProperty(cIdx, pIdx, 'type', e.target.value)}
                          >
                            <option value="line">line</option>
                            <option value="text">text</option>
                            <option value="image">image</option>
                          </select>
                          <button type="button" className="gis-x-btn" onClick={() => removeProperty(cIdx, pIdx)} title="Remove property">×</button>
                        </div>

                        <div className="gis-prop-flags">
                          <label className="gis-flag">
                            <input type="checkbox" checked={!!p.required} onChange={(e) => updateProperty(cIdx, pIdx, 'required', e.target.checked)} />
                            Required
                          </label>
                          <label className="gis-flag">
                            <input type="checkbox" checked={!!p.show_name} onChange={(e) => updateProperty(cIdx, pIdx, 'show_name', e.target.checked)} />
                            Show name
                          </label>
                          {p.type === 'line' && (
                            <>
                              <label className="gis-flag">
                                <input type="checkbox" checked={!!p.as_title} onChange={(e) => updateProperty(cIdx, pIdx, 'as_title', e.target.checked)} />
                                As title
                              </label>
                              <label className="gis-flag">
                                <input type="checkbox" checked={!!p.as_subtitle} onChange={(e) => updateProperty(cIdx, pIdx, 'as_subtitle', e.target.checked)} />
                                As subtitle
                              </label>
                              <span className="gis-meta-inline">
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  style={{ width: 54 }}
                                  value={p.meta?.prefix || ''}
                                  onChange={(e) => updateProperty(cIdx, pIdx, 'meta', { ...(p.meta || {}), prefix: e.target.value })}
                                  placeholder="prefix"
                                />
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  style={{ width: 54 }}
                                  value={p.meta?.suffix || ''}
                                  onChange={(e) => updateProperty(cIdx, pIdx, 'meta', { ...(p.meta || {}), suffix: e.target.value })}
                                  placeholder="suffix"
                                />
                                <select
                                  className="form-control form-control-sm"
                                  style={{ width: 88 }}
                                  value={p.meta?.fontSize || 'text'}
                                  onChange={(e) => updateProperty(cIdx, pIdx, 'meta', { ...(p.meta || {}), fontSize: e.target.value })}
                                >
                                  <option value="heading">heading</option>
                                  <option value="sub-heading">sub-heading</option>
                                  <option value="text">text</option>
                                </select>
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  <button type="button" className="gis-add-prop-btn" onClick={() => addProperty(cIdx)}>+ property</button>
                </div>
              </div>
            ))}
          </>
        )}

        {activeTab === 'mobile' && (
          <>
            {displayMobileProps.length === 0 && (
              <div className="gis-empty-hint">No properties yet. Add properties in the Widescreen tab first.</div>
            )}

            {displayMobileProps.map((mp, idx) => {
              const isDragging = mobileDrag === idx;
              const isOver = mobileOver === idx && mobileDrag !== -1 && !isDragging;
              const type = getMobilePropType(mp);
              return (
                <div
                  key={idx}
                  ref={(el) => { mobileRefs.current[idx] = el; }}
                  className={`gis-mobile-prop${mp.visible ? '' : ' gis-mobile-prop--hidden'}${isDragging ? ' gis-mobile-prop--dragging' : ''}${isOver ? ' gis-mobile-prop--over' : ''}`}
                  draggable
                  onDragStart={(e) => onMobileDragStart(idx, e)}
                  onDragEnter={() => onMobileDragEnter(idx)}
                  onDragOver={onMobileDragOver}
                  onDrop={(e) => onMobileDrop(idx, e)}
                  onDragEnd={onMobileDragEnd}
                >
                  <span className="gis-mobile-drag-handle"><FiMenu size={11} /></span>
                  <input
                    type="checkbox"
                    checked={mp.visible}
                    onChange={(e) => toggleMobileProp(idx, e.target.checked)}
                    title={mp.visible ? 'Hide on mobile' : 'Show on mobile'}
                  />
                  <span className="gis-mobile-prop-name">{getMobilePropLabel(mp)}</span>
                  {type && <span className={`item-prop-type-badge item-prop-type-badge--${type}`}>{type}</span>}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default GalleryItemStructureEditor;
